// Shared flyer detail modal (Flyer Management and other admin surfaces)
import React, { useMemo, useState, useEffect } from 'react';
import { useModalEscapeKey } from '../utils/useModalEscapeKey';
import { useNavigate } from 'react-router-dom';
import { TICKET_CATEGORIES } from '../constants/importPresets';
import { formatHeight, formatWeight } from '../utils/heightWeightUtils';
import { getDisplayName, getFreshlingDisplayName, getAgeFromYear, abbreviateDays } from '../utils/flyerDisplay';
import { HIGH_ALTITUDE_LABEL, isDemandOnlyUsageLabel, purchasedForUsageLabel } from '../utils/ticketUsage';
import { registrationMentorStatusLabel } from '../utils/mentorEligibility';
import {
  mentorApprovalDisplayLabel,
  mentorDaysWorkingDisplay,
  mentorTeamAssignmentLines,
  shirtSizeDisplay,
  volunteerDayDisplay,
} from '../utils/flyerRoleDetails';
import './FlyerManagement.css';
import './admin/FmModal.css';

function telHref(phone) {
  if (!phone || typeof phone !== 'string') return '';
  const digits = phone.replace(/\D/g, '');
  return digits ? `tel:${digits}` : '';
}

function getRecurrencySlotLabel(timeSlotId, slots = []) {
  const slot = slots.find((s) => s.id === timeSlotId);
  if (!slot) return timeSlotId || '';
  if (slot.checkIn && slot.start) return `Check in ${slot.checkIn}, ${slot.start} class begins`;
  return slot.label || slot.start || timeSlotId;
}

function triStateYesNo(v) {
  if (v === true) return 'Yes';
  if (v === false) return 'No';
  return '—';
}

/** Staff-visible Fury axes mirrored from roster sync (when present). */
function furyStructuredAxisRows(flyer) {
  const rows = [];
  const add = (label, value) => {
    if (value == null) return;
    const s = String(value).trim();
    if (s === '') return;
    rows.push({ label, value: s });
  };
  add('Enrollment', flyer.furyEnrollmentStatus);
  add('Payment', flyer.furyRegistrationPaymentStatus);
  add('Lifecycle (withdrawal)', flyer.furyRegistrationLifecycleStatus);
  add('Aggregate approval', flyer.furyRegistrationApprovalStatus);
  if (flyer.furyHasDeniedOfferings === true) {
    const n = flyer.furyDeniedOfferingLineCount;
    rows.push({
      label: 'Partial denial',
      value: n != null && n !== '' ? `Yes (${n} denied line(s))` : 'Yes',
    });
  } else if (flyer.furyHasDeniedOfferings === false) {
    rows.push({ label: 'Partial denial', value: 'No' });
  }
  add('Mentor line outcome', flyer.furyMentorOfferingApprovalStatus);
  add('Checkout completed', flyer.furyCheckoutCompletedAt);
  return rows;
}

/** Optional Fury `participationUnitId` when jump-completion declare is ambiguous. */
function FuryJumpCompletionParticipationUnitRow({ flyer, onSave, saving }) {
  const [val, setVal] = useState(() => (flyer.furyJumpCompletionParticipationUnitId || '').toString());
  useEffect(() => {
    setVal((flyer.furyJumpCompletionParticipationUnitId || '').toString());
  }, [flyer.id, flyer.furyJumpCompletionParticipationUnitId]);
  if (!onSave) return null;
  return (
    <div className="flyer-details-field flyer-details-field--full">
      <strong>Fury participation unit (jump declare)</strong>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
        <input
          type="text"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          placeholder="Only if Declare returns ambiguous unit error"
          aria-label="Fury participation unit id for jump completion"
          style={{ flex: '1 1 12rem', minWidth: 0, maxWidth: '100%' }}
        />
        <button
          type="button"
          className="flyer-details-link-btn"
          disabled={saving}
          onClick={() => onSave(flyer.id, val)}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
      <small style={{ display: 'block', marginTop: 4, color: '#666' }}>
        Sent as participationUnitId to Fury when set. Clear and Save to remove.
      </small>
    </div>
  );
}

export default function FlyerDetailModal({
  flyer,
  onClose,
  onEdit,
  teams = [],
  flyers = [],
  eventId = '',
  notes = '',
  onNotesChange,
  onSaveNotes,
  savingNotes = false,
  onSaveManifestTickets = null,
  savingManifestTickets = false,
  onSaveManifestCheckInRequired = null,
  savingManifestCheckInRequired = false,
  onSaveFuryJumpCompletionParticipationUnitId = null,
  savingFuryJumpCompletionParticipationUnitId = false,
  recurrencyTimeSlots = [],
  onMentorExcludeFromPairing = null,
  onMentorClearPairingExclusion = null,
  onMentorMarkManualApproved = null,
  onMentorClearManualApproved = null,
}) {
  const navigate = useNavigate();

  const teamMembershipByEmail = useMemo(() => {
    const map = new Map();
    teams.forEach((team) => {
      const mentorEmail = (team.mentor || '').toLowerCase().trim();
      if (mentorEmail) {
        const existing = map.get(mentorEmail) || { asMentor: false, asFreshling: false, teams: [] };
        existing.asMentor = true;
        existing.teams.push(team);
        map.set(mentorEmail, existing);
      }
      (team.freshlings || []).forEach((email) => {
        const key = (email || '').toLowerCase().trim();
        if (!key) return;
        const existing = map.get(key) || { asMentor: false, asFreshling: false, teams: [] };
        existing.asFreshling = true;
        existing.teams.push(team);
        map.set(key, existing);
      });
    });
    return map;
  }, [teams]);

  const flyersByEmail = useMemo(() => {
    const m = new Map();
    flyers.forEach((f) => {
      const e = (f.email || '').toLowerCase().trim();
      if (e) m.set(e, f);
    });
    return m;
  }, [flyers]);

  useModalEscapeKey(!!flyer, onClose);

  if (!flyer) return null;

  const emailKey = (flyer.email || '').toLowerCase().trim();
  const membership = teamMembershipByEmail.get(emailKey);
  const manifestTickets = flyer.manifestTickets || {};
  const ticketLabels = Object.entries(manifestTickets)
    .filter(([, count]) => (count || 0) > 0)
    .sort(([a], [b]) => a.localeCompare(b));
  const usage = flyer.usage || {};
  const haPurchasedTotal = purchasedForUsageLabel(flyer, HIGH_ALTITUDE_LABEL);
  const ticketPurchasedRows = (() => {
    const byLabel = new Map(ticketLabels.map(([l, c]) => [l, l === HIGH_ALTITUDE_LABEL ? haPurchasedTotal : c]));
    if (haPurchasedTotal > 0 && !byLabel.has(HIGH_ALTITUDE_LABEL)) {
      byLabel.set(HIGH_ALTITUDE_LABEL, haPurchasedTotal);
    }
    return [...byLabel.entries()]
      .filter(([, n]) => n > 0)
      .sort(([a], [b]) => a.localeCompare(b));
  })();
  const ticketRowsForDisplay = ticketPurchasedRows.length > 0 ?
    ticketPurchasedRows :
    (Array.isArray(flyer.furyOfferings) ?
      flyer.furyOfferings
        .map((o) => [o.label, Number(o.quantity) || 1])
        .filter(([label, n]) => !!label && n > 0)
        .sort(([a], [b]) => a.localeCompare(b)) :
      []);
  const manifestCheckInRequired = flyer.manifestCheckInRequired === true;
  const eventDayLabels = ['Saturday Competition', 'Jet'].filter((l) => TICKET_CATEGORIES.futureManifest.has(l));
  const hasEventDayManifestNumbers = eventDayLabels.some((label) => {
    const u = usage[label];
    const purchased = (u && u.purchased) ?? manifestTickets[label] ?? 0;
    const used = (u && u.used) || 0;
    return purchased > 0 || used > 0;
  });
  const furyOfferings = Array.isArray(flyer.furyOfferings) ? flyer.furyOfferings : [];
  const registrationDateValue = flyer.furyRegistrationCreatedAt && flyer.furyRegistrationCreatedAt.toDate
    ? flyer.furyRegistrationCreatedAt.toDate()
    : flyer.furyRegistrationCreatedAt instanceof Date
      ? flyer.furyRegistrationCreatedAt
      : flyer.createdAt && flyer.createdAt.toDate
        ? flyer.createdAt.toDate()
        : flyer.createdAt instanceof Date
          ? flyer.createdAt
          : null;

  const recurrencyUpdateDisplay = (() => {
    const ru = flyer.recurrencyUpdate;
    if (!ru) return '—';
    const slotPart = getRecurrencySlotLabel(ru.timeSlotId, recurrencyTimeSlots) || '—';
    const datePart =
      ru.submittedAt && ru.submittedAt.toDate
        ? ru.submittedAt.toDate().toLocaleDateString()
        : '';
    return datePart ? `${slotPart} (${datePart})` : slotPart;
  })();

  const manifestProductsDisplay = (() => {
    const mt = flyer.manifestTickets || {};
    const labels = Object.entries(mt)
      .filter(([, c]) => (c || 0) > 0)
      .map(([l]) => l)
      .sort();
    return labels.length ? labels.join(', ') : '—';
  })();

  const emailsSentDisplay = (() => {
    const keys = Array.isArray(flyer.emailTemplatesSent) ? flyer.emailTemplatesSent : [];
    if (keys.length === 0) return '—';
    return [...new Set(keys)]
      .sort()
      .map((k) => String(k).replace(/_/g, ' '))
      .join(', ');
  })();

  return (
    <div className="fm-modal-overlay" onClick={onClose} role="presentation">
      <div
        className="fm-modal fm-modal--lg flyer-details-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="flyer-detail-modal-title"
      >
        <div className="fm-modal-header">
          <h2 className="fm-modal-title" id="flyer-detail-modal-title">
            Flyer details
          </h2>
          <button type="button" className="fm-modal-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="fm-modal-body flyer-details-modal-body">
          <div className="flyer-details-modal-scroll">
            <div className="flyer-details-grid">
              <details className="flyer-details-accordion" open>
                <summary className="flyer-details-accordion-summary">Contact</summary>
                <div className="flyer-details-accordion-panel">
                  <div className="flyer-details-field">
                    <strong>Name</strong>
                    <div>
                      {flyer.type === 'freshling' ? getFreshlingDisplayName(flyer) : getDisplayName(flyer) || flyer.name}
                    </div>
                  </div>
                  <div className="flyer-details-field">
                    <strong>Email</strong>
                    <div>
                      {flyer.email ? (
                        <a href={`mailto:${flyer.email}`}>{flyer.email}</a>
                      ) : (
                        '—'
                      )}
                    </div>
                  </div>
                  <div className="flyer-details-field">
                    <strong>Phone</strong>
                    <div>
                      {flyer.phone ? (
                        <a href={telHref(flyer.phone)}>{flyer.phone}</a>
                      ) : (
                        '—'
                      )}
                    </div>
                  </div>
                </div>
              </details>

              {flyer.type === 'freshling' && (
              <div className="flyer-details-manifest-strip">
                {onSaveManifestCheckInRequired && (
                  <div className="flyer-details-section">
                    <strong className="flyer-details-section-title">Morning manifest check-in</strong>
                    <div>
                      {manifestCheckInRequired ? 'Flagged for check-in' : 'Not flagged'}
                      <button
                        type="button"
                        style={{ marginLeft: '0.5rem', fontSize: '0.8rem' }}
                        disabled={savingManifestCheckInRequired}
                        onClick={() => onSaveManifestCheckInRequired(flyer.id, !manifestCheckInRequired)}
                      >
                        {savingManifestCheckInRequired
                          ? 'Saving…'
                          : manifestCheckInRequired
                            ? 'Unflag'
                            : 'Flag'}
                      </button>
                    </div>
                  </div>
                )}
                <div className="flyer-details-section">
                  <strong className="flyer-details-section-title">Event Day Manifest</strong>
                  <div>
                    {hasEventDayManifestNumbers ? (
                      <ul className="flyer-details-list">
                        {eventDayLabels.map((label) => {
                          const u = usage[label];
                          const purchased = (u && u.purchased) ?? manifestTickets[label] ?? 0;
                          const used = (u && u.used) || 0;
                          if (purchased === 0 && used === 0) return null;
                          if (isDemandOnlyUsageLabel(label)) {
                            return (
                              <li key={label}>
                                {label}: {purchased} purchased
                              </li>
                            );
                          }
                          return (
                            <li key={label}>
                              {label}: {used} / {purchased} used
                            </li>
                          );
                        })}
                      </ul>
                    ) : null}
                    <div className="flyer-details-links-row">
                      <button
                        type="button"
                        className="flyer-details-link-btn"
                        onClick={() =>
                          navigate(
                            `/admin/event-manifest${eventId ? `?eventId=${encodeURIComponent(eventId)}` : ''}`
                          )
                        }
                      >
                        Event Day Manifest
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              )}

              {flyer.type === 'freshling' && (
              <details className="flyer-details-accordion">
                <summary className="flyer-details-accordion-summary">Registration</summary>
                <div className="flyer-details-accordion-panel flyer-details-accordion-panel--stack">
                  <div className="flyer-details-field-grid">
                    <div className="flyer-details-field">
                      <strong>Registration date</strong>
                      <div>
                        {registrationDateValue ? registrationDateValue.toLocaleString() : '—'}
                      </div>
                    </div>
                    <div className="flyer-details-field">
                      <strong>Status</strong>
                      <div>{flyer.status || '—'}</div>
                    </div>
                    <div className="flyer-details-field">
                      <strong>Type</strong>
                      <div>Freshling</div>
                    </div>
                    <div className="flyer-details-field">
                      <strong>Test</strong>
                      <div>{flyer.test ? 'Test' : '—'}</div>
                    </div>
                    {furyStructuredAxisRows(flyer).length > 0 ? (
                      <div className="flyer-details-field flyer-details-field--full">
                        <strong>Fury registration axes</strong>
                        <div className="flyer-details-field-grid">
                          {furyStructuredAxisRows(flyer).map((row) => (
                            <div className="flyer-details-field" key={row.label}>
                              <strong>{row.label}</strong>
                              <div>{row.value}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                    <div className="flyer-details-field">
                      <strong>
                        {flyer.furyEnrollmentStatus != null && String(flyer.furyEnrollmentStatus).trim() !== ''
                          ? 'Off roster'
                          : 'Cancelled'}
                      </strong>
                      <div>{triStateYesNo(flyer.cancelled)}</div>
                    </div>
                    <div className="flyer-details-field">
                      <strong>Waiting</strong>
                      <div>{triStateYesNo(flyer.waiting)}</div>
                    </div>
                    <div className="flyer-details-field">
                      <strong>Fury registration ID</strong>
                      <div>{flyer.furyRegistrationId || '—'}</div>
                    </div>
                    <FuryJumpCompletionParticipationUnitRow
                      flyer={flyer}
                      onSave={onSaveFuryJumpCompletionParticipationUnitId}
                      saving={savingFuryJumpCompletionParticipationUnitId}
                    />
                    <div className="flyer-details-field">
                      <strong>USPA #</strong>
                      <div>{flyer.uspa || '—'}</div>
                    </div>
                    <div className="flyer-details-field">
                      <strong>A License date</strong>
                      <div>{flyer.aLicenseDate || '—'}</div>
                    </div>
                    <div className="flyer-details-field">
                      <strong>Division</strong>
                      <div>{flyer.division || '—'}</div>
                    </div>
                    <div className="flyer-details-field">
                      <strong>Recurrency</strong>
                      <div>{flyer.recurrency || '—'}</div>
                    </div>
                    <div className="flyer-details-field">
                      <strong>Recurrency update</strong>
                      <div>{recurrencyUpdateDisplay}</div>
                    </div>
                    <div className="flyer-details-field">
                      <strong>A-License?</strong>
                      <div>{flyer.hasALicense || '—'}</div>
                    </div>
                    <div className="flyer-details-field">
                      <strong>How heard</strong>
                      <div>{flyer.howHeard || '—'}</div>
                    </div>
                    <div className="flyer-details-field">
                      <strong>Manifest products</strong>
                      <div>{manifestProductsDisplay}</div>
                    </div>
                    <div className="flyer-details-field">
                      <strong>Emails sent</strong>
                      <div>{emailsSentDisplay}</div>
                    </div>
                  </div>

                  <div className="flyer-details-tickets flyer-details-section">
                    <strong className="flyer-details-section-title">Tickets purchased</strong>
                    <div>
                      {ticketRowsForDisplay.length > 0 ? (
                        <ul className="flyer-details-list">
                          {ticketRowsForDisplay.map(([label, n]) => (
                            <li key={label}>
                              {label} — {n} ticket{n !== 1 ? 's' : ''}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        '—'
                      )}
                    </div>
                  </div>

                  <div className="flyer-details-section">
                    <strong className="flyer-details-section-title">Offerings selected (Fury)</strong>
                    <div>
                      {furyOfferings.length > 0 ? (
                        <ul className="flyer-details-list">
                          {furyOfferings.map((o) => (
                            <li key={`${o.label}-${o.quantity}`}>
                              {o.label}
                              {o.quantity > 1 ? ` x${o.quantity}` : ''}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        '—'
                      )}
                    </div>
                  </div>

                  {onSaveManifestTickets && (
                    <div className="flyer-details-section">
                      <strong className="flyer-details-section-title">Usage-tracked tickets</strong>
                      <ul className="flyer-details-list">
                        {[...TICKET_CATEGORIES.futureManifest].sort().map((label) => {
                          const u = usage[label] || {};
                          const demandOnly = isDemandOnlyUsageLabel(label);
                          const purchased = purchasedForUsageLabel(flyer, label);
                          const used = u.used || 0;
                          return (
                            <li key={`admin-${label}`}>
                              {label}: purchased {purchased}
                              {!demandOnly ? `, used ${used}` : ' (demand only)'}
                              <button
                                type="button"
                                style={{ marginLeft: '0.5rem', fontSize: '0.8rem' }}
                                disabled={savingManifestTickets}
                                onClick={() => {
                                  const ticketsStr = window.prompt(
                                    `Set purchased total for ${label}`,
                                    String(purchased)
                                  );
                                  if (ticketsStr == null) return;
                                  let usedNum = 0;
                                  if (!demandOnly) {
                                    const usedStr = window.prompt(
                                      `Set used count for ${label}`,
                                      String(used)
                                    );
                                    if (usedStr == null) return;
                                    usedNum = Number.parseInt(usedStr, 10);
                                    if (!Number.isFinite(usedNum) || usedNum < 0) return;
                                  }
                                  const ticketsNum = Number.parseInt(ticketsStr, 10);
                                  if (!Number.isFinite(ticketsNum) || ticketsNum < 0) return;
                                  onSaveManifestTickets(flyer.id, label, ticketsNum, usedNum);
                                }}
                              >
                                {savingManifestTickets ? 'Saving…' : 'Edit'}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              </details>
              )}

              {flyer.type === 'mentor' && (
              <details className="flyer-details-accordion" open>
                <summary className="flyer-details-accordion-summary">Mentor</summary>
                <div className="flyer-details-accordion-panel flyer-details-accordion-panel--stack">
                  <div className="flyer-details-field-grid">
                    <div className="flyer-details-field">
                      <strong>Registration date</strong>
                      <div>
                        {registrationDateValue ? registrationDateValue.toLocaleString() : '—'}
                      </div>
                    </div>
                    <div className="flyer-details-field">
                      <strong>Fury registration ID</strong>
                      <div>{flyer.furyRegistrationId || '—'}</div>
                    </div>
                    <div className="flyer-details-field">
                      <strong>Test</strong>
                      <div>{flyer.test ? 'Test' : '—'}</div>
                    </div>
                    {furyStructuredAxisRows(flyer).length > 0 ? (
                      <div className="flyer-details-field flyer-details-field--full">
                        <strong>Fury registration axes</strong>
                        <div className="flyer-details-field-grid">
                          {furyStructuredAxisRows(flyer).map((row) => (
                            <div className="flyer-details-field" key={row.label}>
                              <strong>{row.label}</strong>
                              <div>{row.value}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                    <div className="flyer-details-field">
                      <strong>
                        {flyer.furyEnrollmentStatus != null && String(flyer.furyEnrollmentStatus).trim() !== ''
                          ? 'Off roster'
                          : 'Cancelled'}
                      </strong>
                      <div>{triStateYesNo(flyer.cancelled)}</div>
                    </div>
                    <div className="flyer-details-field">
                      <strong>Waiting</strong>
                      <div>{triStateYesNo(flyer.waiting)}</div>
                    </div>
                    <div className="flyer-details-field">
                      <strong>Role subtype</strong>
                      <div>{(flyer.furyMentorRoleSubtype || '').trim() || '—'}</div>
                    </div>
                    <div className="flyer-details-field">
                      <strong>Approval status</strong>
                      <div>
                        {(() => {
                          const primary = mentorApprovalDisplayLabel(flyer);
                          const secondary = registrationMentorStatusLabel(flyer);
                          return (
                            <>
                              {primary}
                              {secondary && secondary !== primary ? (
                                <span style={{ color: '#666', fontSize: '0.9em' }}>
                                  {' '}
                                  ({secondary})
                                </span>
                              ) : null}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                    <div className="flyer-details-field">
                      <strong>FS jumps</strong>
                      <div>{(flyer.fsJumps || '').toString().trim() || '—'}</div>
                    </div>
                    <div className="flyer-details-field">
                      <strong>FS discipline / specialty</strong>
                      <div>
                        {(flyer.furyFsDiscipline || '').trim() ||
                          (flyer.ratings || '').toString().trim() ||
                          '—'}
                      </div>
                    </div>
                    <div className="flyer-details-field">
                      <strong>Days working</strong>
                      <div>{mentorDaysWorkingDisplay(flyer)}</div>
                    </div>
                    <div className="flyer-details-field">
                      <strong>Shirt size</strong>
                      <div>{shirtSizeDisplay(flyer)}</div>
                    </div>
                    <div className="flyer-details-field flyer-details-field--full">
                      <strong>Fury offerings</strong>
                      <div>
                        {furyOfferings.length > 0 ? (
                          <ul className="flyer-details-list">
                            {furyOfferings.map((o) => (
                              <li key={`${o.label}-${o.quantity}`}>
                                {o.label}
                                {o.quantity > 1 ? ` x${o.quantity}` : ''}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          '—'
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flyer-details-section">
                    <strong className="flyer-details-section-title">Team assignment</strong>
                    <div>
                      {(() => {
                        const lines = mentorTeamAssignmentLines(flyer, membership, flyersByEmail, getDisplayName);
                        if (lines.length) {
                          return (
                            <ul className="flyer-details-list">
                              {lines.map((line, i) => (
                                <li key={i}>{line}</li>
                              ))}
                            </ul>
                          );
                        }
                        return (flyer.team || '').trim() ? (
                          <div>{flyer.team}</div>
                        ) : (
                          '—'
                        );
                      })()}
                    </div>
                  </div>
                  {(onMentorExcludeFromPairing ||
                    onMentorClearPairingExclusion ||
                    onMentorMarkManualApproved ||
                    onMentorClearManualApproved) && (
                    <div className="flyer-details-section">
                      <strong className="flyer-details-section-title">Mentor admin</strong>
                      <div className="flyer-details-mentor-actions">
                        {onMentorExcludeFromPairing && (
                          <button type="button" className="btn-sm" onClick={() => onMentorExcludeFromPairing(flyer)}>
                            Exclude from mentor pairing
                          </button>
                        )}
                        {onMentorClearPairingExclusion && (
                          <button type="button" className="btn-sm" onClick={() => onMentorClearPairingExclusion(flyer)}>
                            Clear FM pairing exclusion
                          </button>
                        )}
                        {onMentorMarkManualApproved && (
                          <button type="button" className="btn-sm" onClick={() => onMentorMarkManualApproved(flyer)}>
                            Mark staff-approved (manual)
                          </button>
                        )}
                        {onMentorClearManualApproved && (
                          <button type="button" className="btn-sm" onClick={() => onMentorClearManualApproved(flyer)}>
                            Clear manual approval override
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </details>
              )}

              {flyer.type === 'volunteer' && (
              <details className="flyer-details-accordion" open>
                <summary className="flyer-details-accordion-summary">Volunteer</summary>
                <div className="flyer-details-accordion-panel flyer-details-accordion-panel--stack">
                  <div className="flyer-details-field-grid">
                    <div className="flyer-details-field">
                      <strong>Volunteer day</strong>
                      <div>{volunteerDayDisplay(flyer)}</div>
                    </div>
                    <div className="flyer-details-field">
                      <strong>Shirt size</strong>
                      <div>{shirtSizeDisplay(flyer)}</div>
                    </div>
                    <div className="flyer-details-field">
                      <strong>Auto-confirmed</strong>
                      <div>
                        {flyer.volunteerAutoConfirmed === false
                          ? 'No'
                          : 'Yes (no approval required)'}
                      </div>
                    </div>
                    {furyStructuredAxisRows(flyer).length > 0 ? (
                      <div className="flyer-details-field flyer-details-field--full">
                        <strong>Fury registration axes</strong>
                        <div className="flyer-details-field-grid">
                          {furyStructuredAxisRows(flyer).map((row) => (
                            <div className="flyer-details-field" key={row.label}>
                              <strong>{row.label}</strong>
                              <div>{row.value}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                    <div className="flyer-details-field">
                      <strong>
                        {flyer.furyEnrollmentStatus != null && String(flyer.furyEnrollmentStatus).trim() !== ''
                          ? 'Off roster'
                          : 'Cancelled'}
                      </strong>
                      <div>{triStateYesNo(flyer.cancelled)}</div>
                    </div>
                    <div className="flyer-details-field">
                      <strong>Waiting</strong>
                      <div>{triStateYesNo(flyer.waiting)}</div>
                    </div>
                    <div className="flyer-details-field flyer-details-field--full">
                      <strong>Fury offerings</strong>
                      <div>
                        {furyOfferings.length > 0 ? (
                          <ul className="flyer-details-list">
                            {furyOfferings.map((o) => (
                              <li key={`${o.label}-${o.quantity}`}>
                                {o.label}
                                {o.quantity > 1 ? ` x${o.quantity}` : ''}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          '—'
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </details>
              )}

              {flyer.type === 'freshling' && (
              <>
              <details className="flyer-details-accordion">
                <summary className="flyer-details-accordion-summary">Preferences</summary>
                <div className="flyer-details-accordion-panel">
                  <div className="flyer-details-field">
                    <strong>Team preference</strong>
                    <div>{flyer.team || '—'}</div>
                  </div>
                  <div className="flyer-details-field">
                    <strong>Home DZ</strong>
                    <div>{flyer.homeDz || '—'}</div>
                  </div>
                  <div className="flyer-details-field">
                    <strong>T-Shirt</strong>
                    <div>{flyer.tShirtSize || '—'}</div>
                  </div>
                  <div className="flyer-details-field">
                    <strong>GSG</strong>
                    <div>{flyer.gsg || '—'}</div>
                  </div>
                  <div className="flyer-details-field">
                    <strong>Height</strong>
                    <div>{flyer.ht ? formatHeight(flyer.ht) : '—'}</div>
                  </div>
                  <div className="flyer-details-field">
                    <strong>Weight</strong>
                    <div>{flyer.wt ? formatWeight(flyer.wt) : '—'}</div>
                  </div>
                  <div className="flyer-details-field">
                    <strong>Age</strong>
                    <div>{getAgeFromYear(flyer.yearBorn) ?? '—'}</div>
                  </div>
                  <div className="flyer-details-field">
                    <strong>Teammates (sheet)</strong>
                    <div>{flyer.teammates || '—'}</div>
                  </div>
                  <div className="flyer-details-field">
                    <strong>Days</strong>
                    <div>{abbreviateDays(flyer.days) || '—'}</div>
                  </div>
                  <div className="flyer-details-field">
                    <strong>Intensity</strong>
                    <div>{flyer.intensity || '—'}</div>
                  </div>
                  <div className="flyer-details-field">
                    <strong>Country</strong>
                    <div>{flyer.country || '—'}</div>
                  </div>
                  <div className="flyer-details-field">
                    <strong>State/Region</strong>
                    <div>{flyer.state || '—'}</div>
                  </div>
                </div>
              </details>

              <details className="flyer-details-accordion">
                <summary className="flyer-details-accordion-summary">Scores</summary>
                <div className="flyer-details-accordion-panel flyer-details-accordion-panel--stack">
                  <div className="flyer-details-field-grid">
                    <div className="flyer-details-field">
                      <strong>Skydives</strong>
                      <div>
                        {flyer.jumpNumber != null && String(flyer.jumpNumber).trim() !== ''
                          ? String(flyer.jumpNumber)
                          : '—'}
                      </div>
                    </div>
                    <div className="flyer-details-field">
                      <strong>Sheet #</strong>
                      <div>{flyer.sheetOrder || '—'}</div>
                    </div>
                    <div className="flyer-details-field">
                      <strong>Tunnel</strong>
                      <div>{flyer.tunnel || '—'}</div>
                    </div>
                    <div className="flyer-details-field">
                      <strong>FS jumps</strong>
                      <div>{flyer.fsJumps || '—'}</div>
                    </div>
                    <div className="flyer-details-field">
                      <strong>Ratings</strong>
                      <div>{flyer.ratings || '—'}</div>
                    </div>
                  </div>
                </div>
              </details>
              </>
              )}

              <details className="flyer-details-accordion">
                <summary className="flyer-details-accordion-summary">
                  {flyer.type === 'freshling' ? 'Team' : 'Admin notes'}
                </summary>
                <div className="flyer-details-accordion-panel flyer-details-accordion-panel--stack">
                  {flyer.type === 'freshling' && (
                  <div className="flyer-details-teams flyer-details-section">
                    <strong className="flyer-details-section-title">Pairings</strong>
                    <div>
                      {membership && membership.teams.length > 0 ? (
                        <ul className="flyer-details-list">
                          {membership.teams.map((team) => {
                            const mentorF = team.mentor
                              ? flyersByEmail.get((team.mentor || '').toLowerCase())
                              : null;
                            const mentorName = mentorF
                              ? getDisplayName(mentorF)
                              : team.mentor || '—';
                            const freshlingNames = (team.freshlings || [])
                              .map((e) => {
                                const f = flyersByEmail.get((e || '').toLowerCase());
                                return f ? getDisplayName(f) : e || '—';
                              })
                              .filter(Boolean);
                            return (
                              <li key={team.id}>
                                <button
                                  type="button"
                                  className="flyer-details-team-link"
                                  onClick={() =>
                                    navigate(
                                      `/admin/team-pairing${eventId ? `?eventId=${encodeURIComponent(eventId)}` : ''}`
                                    )
                                  }
                                >
                                  Team {team.teamNumber ? `#${team.teamNumber}` : '(no number)'} — Mentor:{' '}
                                  {mentorName}
                                  {freshlingNames.length > 0
                                    ? `, Freshlings: ${freshlingNames.join(', ')}`
                                    : ''}
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      ) : (
                        '—'
                      )}
                    </div>
                  </div>
                  )}

                  <div className="flyer-details-notes flyer-details-section">
                    <strong className="flyer-details-section-title">
                      {flyer.type === 'freshling' ? 'Notes' : 'Admin notes'}
                    </strong>
                    <textarea
                      className="flyer-details-notes-input"
                      rows={3}
                      value={notes}
                      onChange={(e) => onNotesChange && onNotesChange(e.target.value)}
                      placeholder={
                        flyer.type === 'freshling'
                          ? 'Team changes, thoughts; kept on re-import'
                          : 'Staff notes; kept on re-import'
                      }
                    />
                    {onSaveNotes && (
                      <div className="flyer-details-notes-actions">
                        <button
                          type="button"
                          className="flyer-details-notes-save fm-modal-btn fm-modal-btn-primary"
                          onClick={onSaveNotes}
                          disabled={savingNotes}
                        >
                          {savingNotes ? 'Saving…' : 'Save notes'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </details>

              <details className="flyer-details-accordion">
                <summary className="flyer-details-accordion-summary">Debug</summary>
                <div className="flyer-details-accordion-panel flyer-details-accordion-panel--stack">
                  <div className="flyer-details-section">
                    <button
                      type="button"
                      className="flyer-details-link-btn"
                      onClick={async () => {
                        const text = JSON.stringify(flyer);
                        try {
                          await navigator.clipboard.writeText(text);
                        } catch (_) {
                          window.prompt('Copy minified JSON', text);
                        }
                      }}
                    >
                      Copy minified JSON
                    </button>
                  </div>
                </div>
              </details>
            </div>
          </div>
        </div>
        <div className="fm-modal-footer flyer-details-modal-footer" role="group" aria-label="Flyer detail actions">
          {onEdit && (
            <button type="button" className="fm-modal-btn fm-modal-btn-cancel" onClick={() => onEdit(flyer)}>
              Edit
            </button>
          )}
          <button type="button" className="fm-modal-btn fm-modal-btn-primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
